"use client";

import React from "react";
import Link from "next/link";
import { ShieldCheck, Calendar, ArrowLeft } from "@phosphor-icons/react";

export default function PrivacyPolicy() {
  return (
    <section className="w-full min-h-screen bg-slate-950 text-slate-100 py-16 px-4 md:py-24">
      <div className="max-w-4xl mx-auto space-y-12">
        {/* Navigation back */}
        <Link 
          href="/" 
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-white transition-colors uppercase tracking-wider"
        >
          <ArrowLeft size={16} /> Back to Home
        </Link>

        {/* Title Section */}
        <div className="border-b border-slate-800 pb-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-blue-500/10 text-blue-400">
              <ShieldCheck size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight">Privacy Policy</h1>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-semibold mt-1">SOFAR Education Initiative</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 pt-2">
            <span className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 px-3 py-1 rounded-full">
              <Calendar size={14} className="text-blue-400" />
              Effective Date: June 6, 2026
            </span>
            <span className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 px-3 py-1 rounded-full">
              Version: DPDPA v1.2
            </span>
          </div>
        </div>

        {/* Content Section */}
        <div className="space-y-8 text-sm text-slate-300 leading-relaxed">
          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">1. Introduction</h2>
            <p>
              SOFAR (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) is dedicated to protecting your privacy. This Privacy Policy details how we collect, store, and process your personal information in accordance with India&apos;s **Digital Personal Data Protection (DPDP) Act 2023** and other applicable regional data protection laws.
            </p>
            <p>
              By signing up, enrolling in our courses, or utilizing our services at `thesofar.org`, you explicitly consent to the collection and processing of your personal data as outlined in this policy.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">2. Personal Data We Collect</h2>
            <p>We process only the personal data necessary to provide training resources and verify secure student portal access. This includes:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-white">Identity Details:</strong> Full name, registered email address, and phone number collected during authentication.
              </li>
              <li>
                <strong className="text-white">Academic Details:</strong> Enrolled courses, syllabus modules completed, and examination performance tracking.
              </li>
              <li>
                <strong className="text-white">Usage Records:</strong> Video play position timestamps, log records, browser user-agent signatures, and IP addresses recorded for session security.
              </li>
              <li>
                <strong className="text-white">Communications:</strong> Inquiries sent through support interfaces, contact forms, or login-assistance flows.
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">3. Purpose of Processing Personal Data</h2>
            <p>We utilize the gathered personal information for the following specific purposes:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>To create and manage your secure student dashboard profile.</li>
              <li>To save video playback completion progress (requiring tracked pause/resume timestamps).</li>
              <li>To restrict concurrent streaming logins to protect our intellectual property.</li>
              <li>To notify you about cohort changes, new schedules, or placement assistance updates (subject to your opt-in consent).</li>
            </ul>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">4. Consent and Your Rights under DPDPA 2023</h2>
            <p>
              Under the DPDP Act 2023, you are the **Data Principal**, and SOFAR acts as the **Data Fiduciary**. You possess the following rights regarding your personal data:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong className="text-white">Right to Access:</strong> You can view all personal details collected and a record of active course access directly inside your dashboard.
              </li>
              <li>
                <strong className="text-white">Right to Correction & Erasure:</strong> You may request the update or deletion of your account. Deletion will erase your progress history and deactivate course access.
              </li>
              <li>
                <strong className="text-white">Right to Withdraw Consent:</strong> You have the right to withdraw consent for optional marketing communications (like WhatsApp updates) at any time.
              </li>
              <li>
                <strong className="text-white">Right to Grievance Redressal:</strong> You can contact our Data Protection Officer regarding queries or concerns.
              </li>
            </ul>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">5. Third-Party Data Handlers</h2>
            <p>
              We do not sell or trade student data. We share personal tokens with secure cloud infrastructure nodes (such as Amazon Web Services S3 and CloudFront) strictly to deliver video feeds and track authorization. All third-party handlers are bound by confidentiality clauses.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">6. Refund and Cancellation Notice</h2>
            <p>
              All course purchases are governed by our refund rules. You can find full details on our refund window (7 days) and eligibility criteria (less than 25% content completed) in our <Link href="/terms-and-conditions" className="text-blue-400 hover:underline">Terms and Conditions</Link>.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">7. Security Measures</h2>
            <p>
              We implement industry-grade technical safeguards including end-to-end database hashing, SSL/HTTPS encryption, and signed CloudFront cookies to secure your personal records.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-lg font-bold text-white">8. Contact Us</h2>
            <p>If you have questions, wish to withdraw marketing consent, or file a grievance, please write to our support desk:</p>
            <p className="bg-slate-900 border border-slate-800 p-4 rounded-2xl w-fit space-y-1">
              <span className="block text-xs text-slate-500 font-semibold uppercase tracking-wider">Email Grievance Cell</span>
              <strong className="text-white">sofarindia.helpdesk@gmail.com</strong>
              <span className="block text-xs text-slate-400 mt-1">Phone Helpline: +91 92203 61603</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
